import React, { useEffect, useRef, useState } from "react";

/**
 * Smoothly animates a numeric value from 0 (or previous value) to `value`.
 * Uses requestAnimationFrame with an ease-out cubic curve so the digits
 * appear to tick progressively (1, 2, 3…) rather than jumping in steps.
 */
export const CountUp = ({
    value = 0,
    duration = 1100,
    format = (n) => n.toFixed(0),
    className = "",
    prefix = "",
    suffix = "",
}) => {
    const [display, setDisplay] = useState(0);
    const previousValueRef = useRef(0);
    const rafRef = useRef(null);

    useEffect(() => {
        const start = previousValueRef.current || 0;
        const end = Number(value) || 0;
        if (start === end) {
            setDisplay(end);
            return;
        }
        const startTime = performance.now();

        const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(1, elapsed / duration);
            // ease-out cubic — feels closer to After Effects easing
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = start + (end - start) * eased;
            setDisplay(current);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                previousValueRef.current = end;
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => rafRef.current && cancelAnimationFrame(rafRef.current);
    }, [value, duration]);

    return (
        <span className={className}>
            {prefix}
            {format(display)}
            {suffix}
        </span>
    );
};

export default CountUp;
